import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Calendar } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CountdownTimer = ({ compact = false }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [targetDate, setTargetDate] = useState(null);
  const [timerMessage, setTimerMessage] = useState('Registration Closes In');

  // Load countdown settings from backend
  useEffect(() => {
    const fetchCountdown = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/countdown`);
        if (response.ok) {
          const data = await response.json();
          if (data.target_date) {
            setTargetDate(new Date(data.target_date));
            if (data.message) setTimerMessage(data.message);
          }
        }
      } catch (error) {
        console.error('Error fetching countdown:', error);
      }
    };
    fetchCountdown();
  }, []);

  // Countdown timer logic
  useEffect(() => {
    if (!targetDate) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const isExpired = timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0 && targetDate && new Date() > targetDate;
  const hasNoTimer = !targetDate;

  if (hasNoTimer) {
    return (
      <section className="bg-gradient-to-r from-orange-500 to-red-500 py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Clock size={24} className="text-white" />
            <h2 className="text-xl font-bold text-white">Registration Open</h2>
          </div>
          <p className="text-white/80 mb-4">Join our lay schools today and deepen your understanding of God's Word!</p>
          <Link to="/register">
            <Button className="bg-white text-orange-500 hover:bg-gray-100 font-semibold px-8 py-3 text-lg">
              Register Now
            </Button>
          </Link>
        </div>
      </section>
    );
  }

  if (isExpired) {
    return (
      <section className="bg-gradient-to-r from-orange-500 to-red-500 py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-6">
            <p className="text-2xl font-bold text-white">Registration Has Closed</p>
            <p className="text-white/80 mt-2">Please contact us for more information about upcoming registration periods.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gradient-to-r from-orange-500 to-red-500 py-8 md:py-12 px-4">
      <div className="max-w-4xl mx-auto text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Clock size={compact ? 20 : 28} className="text-white" />
          <h2 className={`${compact ? 'text-lg' : 'text-2xl'} font-bold text-white`}>{timerMessage}</h2>
        </div>
        
        <div className="flex justify-center gap-3 md:gap-6 mb-4">
          {/* Days */}
          <div className={`bg-white/20 backdrop-blur-sm rounded-lg ${compact ? 'p-3 min-w-[60px]' : 'p-4 md:p-6 min-w-[80px] md:min-w-[100px]'}`}>
            <div className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-5xl'} font-bold text-white`}>
              {String(timeLeft.days).padStart(2, '0')}
            </div>
            <div className="text-white/80 text-xs md:text-sm uppercase tracking-wider mt-1">Days</div>
          </div>
          
          {/* Hours */}
          <div className={`bg-white/20 backdrop-blur-sm rounded-lg ${compact ? 'p-3 min-w-[60px]' : 'p-4 md:p-6 min-w-[80px] md:min-w-[100px]'}`}>
            <div className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-5xl'} font-bold text-white`}>
              {String(timeLeft.hours).padStart(2, '0')}
            </div>
            <div className="text-white/80 text-xs md:text-sm uppercase tracking-wider mt-1">Hours</div>
          </div>
          
          {/* Minutes */}
          <div className={`bg-white/20 backdrop-blur-sm rounded-lg ${compact ? 'p-3 min-w-[60px]' : 'p-4 md:p-6 min-w-[80px] md:min-w-[100px]'}`}>
            <div className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-5xl'} font-bold text-white`}>
              {String(timeLeft.minutes).padStart(2, '0')}
            </div>
            <div className="text-white/80 text-xs md:text-sm uppercase tracking-wider mt-1">Minutes</div>
          </div>
          
          {/* Seconds */}
          <div className={`bg-white/20 backdrop-blur-sm rounded-lg ${compact ? 'p-3 min-w-[60px]' : 'p-4 md:p-6 min-w-[80px] md:min-w-[100px]'}`}>
            <div className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-5xl'} font-bold text-white`}>
              {String(timeLeft.seconds).padStart(2, '0')}
            </div>
            <div className="text-white/80 text-xs md:text-sm uppercase tracking-wider mt-1">Seconds</div>
          </div>
        </div>

        {targetDate && (
          <p className="text-white/80 text-sm flex items-center justify-center gap-2 mb-4">
            <Calendar size={14} />
            Registration closes on {targetDate.toLocaleDateString('en-US', { 
              weekday: 'short', 
              month: 'short', 
              day: 'numeric',
              year: 'numeric'
            })}
          </p>
        )}

        <Link to="/register">
          <Button className="bg-white text-orange-500 hover:bg-gray-100 font-semibold px-8 py-3 text-lg">
            Register Now
          </Button>
        </Link>
      </div>
    </section>
  );
};

export default CountdownTimer;
